import { useState, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';

/**
 * Custom hook for animating a truck along a route with waypoints
 */
export const useTruckAnimation = (mapRef) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentWaypointIndex, setCurrentWaypointIndex] = useState(0);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [waypoints, setWaypoints] = useState([]); // [{lat, lng, name, type: 'source'|'facility'|'destination'}]
  const [routeGeometry, setRouteGeometry] = useState(null);
  
  const truckMarkerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const waypointMarkersRef = useRef([]);

  // Create truck marker
  const createTruckMarker = useCallback((lng, lat) => {
    if (!mapRef.current) {
      return null;
    }

    // Remove existing truck marker
    if (truckMarkerRef.current) {
      truckMarkerRef.current.remove();
      truckMarkerRef.current = null;
    }

    const el = document.createElement('div');
    el.className = 'truck-marker moving';
    el.innerHTML = `
      <div class="truck-icon">
        🚛
      </div>
    `;

    const marker = new maplibregl.Marker({
      element: el,
      anchor: 'center',
      rotationAlignment: 'map',
      pitchAlignment: 'map'
    })
      .setLngLat([lng, lat])
      .addTo(mapRef.current);

    truckMarkerRef.current = marker;
    return marker;
  }, [mapRef]);

  // Add waypoint markers
  const addWaypointMarkers = useCallback((waypointsList) => {
    if (!mapRef.current) return;

    // Clear existing waypoint markers
    waypointMarkersRef.current.forEach(marker => marker.remove());
    waypointMarkersRef.current = [];

    waypointsList.forEach((waypoint, idx) => {
      const el = document.createElement('div');
      el.className = 'waypoint-marker-container';
      
      let color, label, icon;
      if (waypoint.type === 'source') {
        color = '#34c759';
        label = 'Start';
        icon = '🏁';
      } else if (waypoint.type === 'destination') {
        color = '#ff3b30';
        label = 'End';
        icon = '🎯';
      } else {
        color = '#007bff';
        label = `Stop ${idx}`;
        icon = '🏢';
      }

      el.innerHTML = `
        <div class="waypoint-marker" style="background: ${color}; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); font-size: 16px;">
          ${icon}
        </div>
        <div class="waypoint-label" style="margin-top: 4px; background: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; box-shadow: 0 2px 4px rgba(0,0,0,0.2); white-space: nowrap;">
          ${waypoint.name || label}
        </div>
      `;

      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'bottom'
      })
        .setLngLat([waypoint.lng, waypoint.lat])
        .addTo(mapRef.current);

      waypointMarkersRef.current.push(marker);
    });
  }, [mapRef]);

  // Animate truck along route
  const animateTruck = useCallback((geometry, duration = 30000) => {
    if (!mapRef.current || !geometry || !geometry.coordinates) {
      return;
    }

    const coordinates = geometry.coordinates;
    if (coordinates.length < 2) {
      return;
    }

    setIsAnimating(true);
    setCurrentWaypointIndex(0);
    setAnimationProgress(0);

    // Create truck at start position
    const [startLng, startLat] = coordinates[0];
    
    const truck = createTruckMarker(startLng, startLat);
    if (!truck) {
      return;
    }

    let startTime = null;
    const totalPoints = coordinates.length;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position index
      const exactPosition = progress * (totalPoints - 1);
      const currentIndex = Math.floor(exactPosition);
      const segmentProgress = exactPosition - currentIndex;

      if (currentIndex < totalPoints - 1 && truck) {
        const [lng1, lat1] = coordinates[currentIndex];
        const [lng2, lat2] = coordinates[currentIndex + 1];

        // Interpolate position between two points
        const currentLng = lng1 + (lng2 - lng1) * segmentProgress;
        const currentLat = lat1 + (lat2 - lat1) * segmentProgress;

        // Update truck position
        try {
          truck.setLngLat([currentLng, currentLat]);

          // No rotation needed for circular truck

          setAnimationProgress(progress);
        } catch (e) {
          // Error updating truck position
        }
      }

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        setAnimationProgress(1);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [mapRef, createTruckMarker]);

  // Stop animation
  const stopAnimation = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setIsAnimating(false);
  }, []);

  // Clear all truck-related markers
  const clearTruckAnimation = useCallback(() => {
    stopAnimation();
    
    if (truckMarkerRef.current) {
      truckMarkerRef.current.remove();
      truckMarkerRef.current = null;
    }

    waypointMarkersRef.current.forEach(marker => marker.remove());
    waypointMarkersRef.current = [];

    setWaypoints([]);
    setRouteGeometry(null);
    setCurrentWaypointIndex(0);
    setAnimationProgress(0);
  }, [stopAnimation]);

  return {
    isAnimating,
    currentWaypointIndex,
    animationProgress,
    waypoints,
    setWaypoints,
    routeGeometry,
    setRouteGeometry,
    truckMarkerRef,
    waypointMarkersRef,
    addWaypointMarkers,
    animateTruck,
    stopAnimation,
    clearTruckAnimation,
  };
};

